import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { QuizAnalytics } from '../analytics/QuizAnalytics';
import {
  handToCombo,
  handToClass,
  getSuitedness,
  getHandCategory,
  getRankCluster,
  deriveAction,
  getErrorType,
  deriveScenarioNode,
} from '../utils/handUtils';

const VALID_BET_TYPES = {
  OPEN: 'OPEN',
  FACING_OPEN_CALL: 'FACING_OPEN_CALL',
  FACING_OPEN_3BET: 'FACING_OPEN_3BET',
  FACING_3BET_CALL: 'FACING_3BET_CALL',
  FACING_3BET_4BET: 'FACING_3BET_4BET',
  FACING_4BET_CALL: 'FACING_4BET_CALL',
  SQUEEZE_4BET: 'SQUEEZE_4BET',
  CALL_VS_3BET: 'CALL_VS_3BET',
  LIMP: 'LIMP',         // Add this line
  SB_LIMP: 'SB_LIMP'    // Add this line
};

const DEFAULT_GAME_CONFIG = {
  betType: 'OPEN',
  heroPosition: 'LJ',
  openPosition: 'LJ', // Set default openPosition to match heroPosition for OPEN type
  threeBetPosition: '',
  totalHands: 10,
  range: `/database/preflop/ranges/lj/lj_open.csv`
};

export const GameContext = createContext();

const recordAnswer = (hand, action, isCorrect) => {
  console.log('recordAnswer called:', gameState.questionCount);
  
  setGameState(prev => {
    // Record the answer in quiz analytics first
    try {
      quizAnalytics.recordAnswer(
        prev.gameConfig.betType,
        prev.gameConfig.heroPosition,
        hand,
        action,
        isCorrect
      );
    } catch (error) {
      console.error('Error recording answer:', error);
    }

    return {
      ...prev,
      questionCount: prev.questionCount + 1,
      correctAnswers: isCorrect ? prev.correctAnswers + 1 : prev.correctAnswers,
      lastAction: action,
      isCorrect,
      currentHand: hand
    };
  });
};

export const GameProvider = ({ children }) => {
  // Add the missing state variable for quiz processed
  const [quizProcessed, setQuizProcessed] = useState(false);
  const navigate = useNavigate();
  const [quizSaveState, setQuizSaveState] = useState({
    lastSavedQuizId: null,
    isSaving: false
  });

  const finalizeQuiz = async () => {
    setQuizFinalized(true);
    
    // If quiz is already saved, don't save again
    if (quizSaveState.lastSavedQuizId) {
      return;
    }
    
    try {
      setQuizSaveState(prev => ({ ...prev, isSaving: true }));
      
      // Use finalizeCurrentQuiz to save the quiz results
      const success = await quizAnalytics.finalizeCurrentQuiz();
      
      if (success) {
        console.log('Quiz finalized successfully');
        const stats = quizAnalytics.getOverallStats();
        console.log('Updated stats:', stats);
        
        setQuizSaveState({
          lastSavedQuizId: Date.now().toString(),
          isSaving: false
        });
      }
    } catch (error) {
      console.error('Failed to finalize quiz:', error);
      setQuizSaveState(prev => ({ ...prev, isSaving: false }));
    }
  };
  
  // Initialize QuizAnalytics
  const [quizAnalytics] = useState(() => {
    console.log('Creating QuizAnalytics instance');
    const instance = new QuizAnalytics();
    console.log('QuizAnalytics instance created:', instance);
    return instance;
  });

  const [quizFinalized, setQuizFinalized] = useState(false);

  const [gameState, setGameState] = useState({
    questionCount: 0,
    correctAnswers: 0,
    currentHand: null,
    lastAction: null,
    isCorrect: false,
    pendingNavigation: null,
    gameConfig: DEFAULT_GAME_CONFIG,
    currentRange: new Map(),
    currentRangeData: null
  });

  // ── Cross-navigation hand-repeat tracking ─────────────────────────────────
  // PreflopTable unmounts/remounts on every question (it navigates to /answer
  // and back to /preflop), so a ref local to that component can't remember
  // which hands were already shown. GameProvider wraps the router and never
  // unmounts mid-quiz, so tracking lives here instead.
  //
  // Rule: once a hand class (e.g. "AKs", "77", "A5o") is shown and answered
  // correctly, it will not reappear for the rest of the quiz. If answered
  // incorrectly, it's held back for a couple hands and then DOES come back —
  // guaranteed, not just eligible to be redealt by chance — so the user gets
  // another crack at it.
  const usedHandKeysRef = useRef(new Set()); // banned rank-pair keys, e.g. "AK", "77"
  const retryQueueRef = useRef([]); // [{ fullClass, key, dueAtQuestionCount }]
  const HAND_REPEAT_DELAY = 2; // hands to wait before a missed hand is forced back

  const resetHandTracking = useCallback(() => {
    usedHandKeysRef.current = new Set();
    retryQueueRef.current = [];
    blastLiveHandsRef.current = 0;
  }, []);

  // Call right after a hand is dealt, so its rank pair is excluded from
  // future draws this quiz (e.g. showing "AKs" bans "AKo" too — same rule as
  // pairs, which only have one class anyway).
  const markHandShown = useCallback((fullClass) => {
    if (!fullClass) return;
    usedHandKeysRef.current.add(fullClass.slice(0, 2));
  }, []);

  // Call once the answer for that hand has been graded.
  const markHandResult = useCallback((fullClass, isCorrect) => {
    if (!fullClass || isCorrect) return;
    // Wrong answer: queue this exact hand class to be forced back in a few hands.
    const naturalDue = gameState.questionCount + 1 + HAND_REPEAT_DELAY;
    const totalHands = gameState.gameConfig?.totalHands;
    // A wrong answer late in the quiz (e.g. question 8-10 of 10) would
    // naturally come due AFTER the quiz already ends, so it would silently
    // never resurface at all. Every wrong answer must get a guaranteed
    // repeat within the same quiz, so pull the due question back to the
    // last available slot instead of letting it fall off the end.
    const dueAtQuestionCount = (totalHands && naturalDue >= totalHands)
      ? Math.max(gameState.questionCount + 1, totalHands - 1)
      : naturalDue;
    retryQueueRef.current.push({
      fullClass,
      key: fullClass.slice(0, 2),
      dueAtQuestionCount
    });
  }, [gameState.questionCount, gameState.gameConfig]);

  // Call right before generating the next hand. If a wrong-answer hand's
  // wait period has elapsed, this reports it as forcedHandClass so the
  // caller deals exactly that hand class next, guaranteeing the return
  // rather than just allowing it.
  //
  // This is a non-destructive PEEK — it does not remove the item from the
  // queue or un-ban its key. It used to do both right here, but that made
  // the queue item's consumption tied to how many times this function got
  // *called*, not how many times a hand actually got rendered to the user.
  // Under React 18 StrictMode (see index.jsx), effects in development are
  // invoked twice on mount: the first pass would pop the due item and kick
  // off generating that hand, then get thrown away by StrictMode's
  // simulated remount before its `isMounted` check let it call
  // setHeroCards; the second pass would find the queue already empty and
  // deal an unrelated random hand instead — silently eating the missed
  // hand's guaranteed return every time. Callers must now call
  // commitHandDirective(forcedHandClass) themselves, and only once they've
  // confirmed (via their own isMounted check) that they're actually
  // rendering that hand — see PreflopTable / BlastTable.
  const getNextHandDirective = useCallback(() => {
    const currentCount = gameState.questionCount;
    const idx = retryQueueRef.current.findIndex(item => currentCount >= item.dueAtQuestionCount);
    const forcedHandClass = idx !== -1 ? retryQueueRef.current[idx].fullClass : null;
    return { forcedHandClass, usedHandKeys: usedHandKeysRef.current };
  }, [gameState.questionCount]);

  // Call once a forced-return hand has actually been committed to state
  // (i.e. inside the caller's `isMounted` guard, right where markHandShown
  // is called) — removes it from the retry queue and un-bans its key for
  // good. See getNextHandDirective for why this can't happen at peek time.
  const commitHandDirective = useCallback((forcedHandClass) => {
    if (!forcedHandClass) return;
    const idx = retryQueueRef.current.findIndex(item => item.fullClass === forcedHandClass);
    if (idx !== -1) {
      usedHandKeysRef.current.delete(retryQueueRef.current[idx].key);
      retryQueueRef.current.splice(idx, 1);
    }
  }, []);

  // ── Blast "live decision" quota ───────────────────────────────────────────
  // Blast range CSVs omit any hand whose correct action is always fold — so
  // sampling only from the CSV (the old behavior) meant every dealt hand was
  // guaranteed a real decision, and genuine trash never appeared. That's the
  // opposite problem from pure random dealing against a narrow range (e.g. a
  // short-stack jam range), where fold is correct on most hands and a
  // 10-question quiz can easily turn into nothing but folds.
  //
  // This quota guarantees at least BLAST_TARGET_LIVE_FRACTION of a quiz's
  // hands come from the curated "real decision" pool, while the rest are
  // dealt from the full reachable hand space (so genuine folds show up) —
  // self-adjusting per stack/range without hardcoding per-depth rules: a wide
  // range naturally produces enough live hands on its own and rarely needs to
  // force; a narrow one (short stack, isolated reaction) forces more often.
  const blastLiveHandsRef = useRef(0);
  const BLAST_TARGET_LIVE_FRACTION = 0.3;

  // Call before generating the next Blast hand to decide whether it must be
  // forced from the curated pool this time.
  const shouldForceBlastLiveHand = useCallback((totalHands) => {
    if (!totalHands) return false;
    const targetLiveTotal = Math.ceil(totalHands * BLAST_TARGET_LIVE_FRACTION);
    const remaining = Math.max(1, totalHands - gameState.questionCount);
    const neededLive = Math.max(0, targetLiveTotal - blastLiveHandsRef.current);
    if (neededLive <= 0) return false;
    if (neededLive >= remaining) return true;
    // Spread forced picks across the remaining questions instead of always
    // waiting until the last possible moment, so the pattern isn't guessable.
    return Math.random() < (neededLive / remaining);
  }, [gameState.questionCount]);

  // Call after a Blast hand is dealt, reporting whether it ended up being a
  // real ("live") decision hand, to keep the running quota accurate.
  const markBlastHandDealt = useCallback((wasLive) => {
    if (wasLive) blastLiveHandsRef.current += 1;
  }, []);

  const startNewGame = async (config) => {
    // Add debug logging for incoming config
    console.log('Starting new game with initial config:', {
      ...config,
      betType: config.betType || 'OPEN',
      heroPosition: config.heroPosition,
      openPosition: config.openPosition,
      threeBetPosition: config.threeBetPosition,
      callType: config.callType
    });

    // A new quiz is starting — clear the hand-repeat tracking from any prior quiz.
    resetHandTracking();

    try {
      // Default the config values with improved validation
      const defaultedConfig = {
        betType: config.betType || 'OPEN',
        heroPosition: config.heroPosition || 'LJ',
        openPosition: config.openPosition || (config.betType === 'OPEN' ? config.heroPosition : ''),
        threeBetPosition: config.threeBetPosition || '',
        totalHands: config.totalHands || DEFAULT_GAME_CONFIG.totalHands,
        callType: config.callType || '',
        isVs3bet: config.isVs3bet || false
      };
  
      // *** NEW CODE: Special case for LIMP scenarios ***
      if (defaultedConfig.betType === 'LIMP' || defaultedConfig.betType === 'SB_LIMP' ||
          (defaultedConfig.heroPosition === 'SB' && defaultedConfig.betType?.toUpperCase().includes('LIMP'))) {
        // Standardize the LIMP bet type
        defaultedConfig.betType = 'LIMP';
        defaultedConfig.heroPosition = 'SB';  // LIMP is always from SB
        console.log('Detected LIMP scenario, standardized config:', defaultedConfig);
      }
  
      // For OPEN type, always set openPosition to heroPosition  
      if (defaultedConfig.betType === 'OPEN') {
        defaultedConfig.openPosition = defaultedConfig.heroPosition;
      }
      
      // Handle all FACING_3BET_CALL scenarios together
      if (defaultedConfig.betType === 'FACING_3BET_CALL' || 
        (defaultedConfig.betType === 'CALL' && defaultedConfig.isVs3bet)) {
      
      console.log('3bet call scenario with EXPLICIT roles:', defaultedConfig);
      
      // Always ensure we're using the correct bet type
      defaultedConfig.betType = 'FACING_3BET_CALL';
      
      // SIMPLIFIED ASSIGNMENT - if hero is already the opener (from QuizConfigScreen),
      // then don't override positions unnecessarily
      
      // If openPosition isn't set to the hero yet, do it now
      if (defaultedConfig.openPosition !== defaultedConfig.heroPosition) {
        console.log('Setting openPosition to hero (original opener):', defaultedConfig.heroPosition);
        defaultedConfig.openPosition = defaultedConfig.heroPosition;
      }
      
      // Validate that 3bettor position is set and valid
      if (!defaultedConfig.threeBetPosition) {
        console.warn('Missing 3bettor position, using fallback');
        // Pick a reasonable position different from hero
        const allPositions = ['LJ', 'HJ', 'CO', 'BTN', 'SB', 'BB'];
        const validPositions = allPositions.filter(pos => pos !== defaultedConfig.heroPosition);
        defaultedConfig.threeBetPosition = validPositions[0];
      }
      
      // Final validation - positions can't be the same
      if (defaultedConfig.openPosition === defaultedConfig.threeBetPosition) {
        console.error('CRITICAL: openPosition equals threeBetPosition, fixing...');
        
        const allPositions = ['LJ', 'HJ', 'CO', 'BTN', 'SB', 'BB'];
        const validPositions = allPositions.filter(pos => pos !== defaultedConfig.heroPosition);
        
        if (validPositions.length > 0) {
          defaultedConfig.threeBetPosition = validPositions[0];
        }
      }
      
      console.log('Final 3bet call config with explicit roles:', {
        heroPosition: defaultedConfig.heroPosition,  // Hero (also the opener in this scenario)
        openPosition: defaultedConfig.openPosition,  // Should be same as hero
        threeBetPosition: defaultedConfig.threeBetPosition,  // The 3bettor
        betType: defaultedConfig.betType
      });
    }
  
      // Only start new quiz if this isn't a reset
      if (config.resetType !== 'FULL_RESET') {
        await quizAnalytics.startNewQuiz(defaultedConfig);
      }
      
      setQuizProcessed(false);
      
      setGameState(prev => {
        if (config.resetType === 'FULL_RESET') {
          return {
            ...prev,
            questionCount: 0,
            correctAnswers: 0,
            currentHand: null,
            lastAction: null,
            gameConfig: DEFAULT_GAME_CONFIG
          };
        }
  
        if (!VALID_BET_TYPES[defaultedConfig.betType]) {
          console.error(`Invalid bet type: ${defaultedConfig.betType}`);
          return prev;
        }
        
        // IMPORTANT FIX: Construct the correct range path based on bet type
        const getRangePath = () => {
          const { betType, heroPosition, openPosition, threeBetPosition, isVs3bet, callType } = defaultedConfig;
          const pos = heroPosition.toLowerCase();
          const baseFolder = `/database/preflop/ranges/${pos}`;
          
          // Add explicit logging for range path construction
          console.log('Constructing range path with:', {
            betType,
            heroPosition,
            openPosition,
            threeBetPosition,
            isVs3bet,
            callType,
            baseFolder
          });
          
          // Special case for LJ call vs 3bet
          if (heroPosition === 'LJ' && betType === 'FACING_3BET_CALL') {
            // Get the 3bettor position correctly
            const tPos = threeBetPosition?.toLowerCase() || 'btn';
            console.log(`Creating LJ call vs 3bet range path for 3bettor: ${tPos}`);
            return `${baseFolder}/${pos}_call-${tPos}_3bet.csv`;
          }

          if (betType === 'LIMP' || betType === 'SB_LIMP' || 
            (heroPosition === 'SB' && betType?.toUpperCase().includes('LIMP'))) {
          console.log('Creating SB limp range path');
          return `/database/preflop/ranges/sb/sb_limp.csv`;
        }
          
          switch (betType) {
            case 'OPEN':
              return `${baseFolder}/${pos}_open.csv`;
              
            case 'FACING_OPEN_CALL':
              if (!openPosition) {
                console.warn('Missing openPosition for FACING_OPEN_CALL, using default open range');
                return `${baseFolder}/${pos}_open.csv`;
              }
              return `${baseFolder}/${pos}_call-${openPosition.toLowerCase()}_open.csv`;
              
            case 'FACING_OPEN_3BET':
              if (!openPosition) {
                console.warn('Missing openPosition for FACING_OPEN_3BET, using default open range');
                return `${baseFolder}/${pos}_open.csv`;
              }
              return `${baseFolder}/${pos}_3bet-${openPosition.toLowerCase()}_open.csv`;
              
            case 'FACING_3BET_4BET':
              const threePos = threeBetPosition?.toLowerCase() || 'bb';
              console.log(`Using 3bet position for 4bet range: ${threePos}`);
              return `${baseFolder}/${pos}_4bet-${threePos}_3bet.csv`;
              
              case 'FACING_3BET_CALL':
                // Enhanced 3bet call handling - always use threeBetPosition
                const tPos = threeBetPosition?.toLowerCase() || 'btn';
                console.log(`Using 3bet position for call range: ${tPos}`);
                console.log('Full call vs 3bet config:', {
                  heroPosition: heroPosition,
                  openPosition: openPosition, // Should be same as heroPosition
                  threeBetPosition: threeBetPosition, // Should be opponent
                  rangePath: `${baseFolder}/${pos}_call-${tPos}_3bet.csv`
                });
                return `${baseFolder}/${pos}_call-${tPos}_3bet.csv`;
              
            default:
              console.error(`Unknown bet type: ${betType}, falling back to open range`);
              return `${baseFolder}/${pos}_open.csv`;
          }
        };
        
        const rangePath = getRangePath();
        console.log('Generated range path:', rangePath);
        
        const newGameConfig = {
          ...DEFAULT_GAME_CONFIG,
          // Spread the original incoming config first so Blast-specific fields
          // (format, position, stack, action, subtype, villainPos, villainAction,
          //  filterMode, quizType, etc.) are preserved in gameState.
          ...config,
          // Then apply the normalised cash-game fields on top.
          ...defaultedConfig,
          range: rangePath
        };
        
        console.log('Final game config:', newGameConfig);
        
        return {
          ...prev,
          questionCount: 0,
          correctAnswers: 0,
          currentHand: null,
          lastAction: null,
          gameConfig: newGameConfig
        };
      });
  
    } catch (error) {
      console.error('Error starting new game:', error);
    }
  };

  // Load range data helper function
  const loadRange = async (rangePath) => {
    try {
      const response = await fetch(rangePath);
      if (!response.ok) {
        throw new Error('Failed to load range file');
      }
      const text = await response.text();
      const lines = text.trim().split('\n');
      
      const newRange = new Map();
      const rangeData = [];
      
      const startIndex = lines[0].toLowerCase().includes('hand') ? 1 : 0;
      
      for (let i = startIndex; i < lines.length; i++) {
        const [hand, type, freq] = lines[i].split(',').map(v => v.trim());
        if (!hand || !freq) continue;
        
        const frequency = parseInt(freq.replace('%', ''));
        if (isNaN(frequency)) continue;
        
        newRange.set(hand, frequency);
        rangeData.push({ hand, type, frequency });
      }
      
      return { newRange, rangeData };
    } catch (error) {
      console.error('Error loading range:', error);
      return { newRange: new Map(), rangeData: [] };
    }
  };
  
  // Format hand helper
  const formatHand = (hand) => {
    if (!hand?.card1Value || !hand?.card2Value) return '';
    
    const suited = hand.card1Suit === hand.card2Suit;
    const card1 = hand.card1Value;
    const card2 = hand.card2Value;
    
    if (card1 === card2) {
      return `${card1}${card2}`;
    }
    
    // Get card ranks
    const ranks = { '2': 2, '3': 3, '4': 4, '5': 5, '6': 6, '7': 7, '8': 8, '9': 9, 'T': 10, 'J': 11, 'Q': 12, 'K': 13, 'A': 14 };
    const rank1 = ranks[card1] || 0;
    const rank2 = ranks[card2] || 0;
    
    if (rank1 >= rank2) {
      return `${card1}${card2}${suited ? 's' : 'o'}`;
    } else {
      return `${card2}${card1}${suited ? 's' : 'o'}`;
    }
  };

  /**
   * Blast-specific answer recorder.
   * Increments questionCount / correctAnswers and records in analytics
   * WITHOUT setting pendingNavigation (BlastTable navigates itself).
   */
  const recordBlastAnswer = async (isCorrect, hand) => {
    const cfg  = gameState.gameConfig;
    // BlastTable passes heroCards directly; fall back to gameState.currentHand just in case
    hand = hand || gameState.currentHand;
    const isLast = (gameState.questionCount + 1) >= (cfg.totalHands || 10);

    // Record analytics outside setGameState so we can await finalization after
    try {
      const combo      = handToCombo(hand);
      const handClass  = handToClass(hand);
      const suitedness = getSuitedness(hand);
      const category   = getHandCategory(handClass);
      const cluster    = getRankCluster(handClass);
      const node       = deriveScenarioNode(cfg);

      const selectedFreq   = isCorrect ? 100 : 0;
      const selectedAction = isCorrect ? 'Jam' : 'Fold';

      quizAnalytics.recordAnswer({
        // Legacy compat
        position:         cfg.heroPosition || cfg.position,
        hand:             hand,
        chosenFrequency:  selectedFreq,
        correctFrequency: 100,
        isCorrect:        isCorrect,

        // Rich fields
        quiz_type:        'BLAST',
        game_format:      cfg.format || null,
        stack_depth:      cfg.stack  || null,
        hero_position:    cfg.heroPosition || cfg.position || null,
        villain_position: cfg.villainPos   || null,
        scenario_node:    node,
        action_type:      cfg.action || cfg.subtype || null,
        training_filter:  cfg.filterMode || null,
        bet_type:         cfg.betType || 'BLAST',

        hand_combo:       combo,
        hand_class:       handClass,
        suitedness:       suitedness,
        hand_category:    category,
        rank_cluster:     cluster,

        correct_action:   'Jam',
        correct_frequency: null,
        selected_action:  selectedAction,
        selected_frequency: selectedFreq,
        is_correct:       isCorrect,
        error_type:       isCorrect ? null : 'wrong_action',
        answered_at:      new Date().toISOString(),
        question_number:  gameState.questionCount + 1,
      });
    } catch (err) {
      console.warn('recordBlastAnswer: analytics error (non-fatal)', err);
    }

    setGameState(prev => ({
      ...prev,
      questionCount:  prev.questionCount + 1,
      correctAnswers: isCorrect ? prev.correctAnswers + 1 : prev.correctAnswers,
      isCorrect
    }));

    // Finalize analytics on the last question so results screen can read it
    if (isLast) {
      try {
        const finalized = await quizAnalytics.finalizeCurrentQuiz();
        console.log('[blast] quiz finalized:', finalized);
      } catch (err) {
        console.warn('[blast] finalization error (non-fatal):', err);
      }
    }
  };

  const checkAnswer = async (hand, action, rangePath) => {
    try {
      console.log('checkAnswer started:', gameState.questionCount);
      console.log('Hand data being checked:', hand);
      console.log('Range path being used:', rangePath);
      console.log('Action being checked:', action);
      console.log('Current game config:', gameState.gameConfig);
      
      const [chosenFrequencyStr, actionType] = action.split('_');
      const chosenFrequency = parseInt(chosenFrequencyStr, 10);
      
      if (isNaN(chosenFrequency)) {
        console.error(`Invalid frequency value: ${chosenFrequencyStr}`);
        return { isCorrect: false, actualFrequency: 0 };
      }
      
      // For 4bet scenarios, use special handling
      if (gameState.gameConfig.betType === 'FACING_3BET_4BET') {
        console.log('Checking 4bet answer with range path:', rangePath);
        console.log('4bet config details:', {
          heroPosition: gameState.gameConfig.heroPosition,
          threeBetPosition: gameState.gameConfig.threeBetPosition || '[not set]',
          openPosition: gameState.gameConfig.openPosition || '[not set]'
        });
        
        // Load the 4bet range data
        const { newRange, rangeData } = await loadRange(rangePath);
        const formattedHand = formatHand(hand);
        
        console.log('Formatted hand for 4bet check:', formattedHand);
        console.log('4bet range data loaded with hands:', newRange.size);
        
        // Debug: Log the first few hands in the range
        let handCount = 0;
        console.log('Sample hands from range:');
        for (const [key, value] of newRange.entries()) {
          if (handCount < 5) {
            console.log(`- ${key}: ${value}%`);
            handCount++;
          } else {
            break;
          }
        }
        
        // Get actual frequency from the 4bet range
        let actualFrequency = newRange.has(formattedHand) ? newRange.get(formattedHand) : 0;
        
        console.log('4bet actual frequency for hand:', { 
          hand: formattedHand, 
          frequency: actualFrequency,
          chosen: chosenFrequency
        });
        
        // Determine correctness
        let isCorrect = false;
        if (chosenFrequency === 0) {
          isCorrect = actualFrequency === 0;
        } else if (chosenFrequency === 50) {
          isCorrect = actualFrequency > 0 && actualFrequency < 100;
        } else if (chosenFrequency === 100) {
          isCorrect = actualFrequency === 100;
        }
        
        // Record answer in QuizAnalytics (rich form)
        try {
          const cfg           = gameState.gameConfig;
          const combo         = handToCombo(hand);
          const handClass     = handToClass(hand);
          const suitedness    = getSuitedness(hand);
          const category      = getHandCategory(handClass);
          const cluster       = getRankCluster(handClass);
          const node          = deriveScenarioNode(cfg);
          const correctAction  = deriveAction(cfg.betType, actualFrequency);
          const selectedAction = deriveAction(cfg.betType, chosenFrequency);
          const errorType      = isCorrect ? null : getErrorType(correctAction, selectedAction);

          const recorded = await quizAnalytics.recordAnswer({
            // Legacy compat
            position:         cfg.heroPosition,
            hand:             hand,
            chosenFrequency:  chosenFrequency,
            correctFrequency: actualFrequency,
            isCorrect:        isCorrect,

            // Rich fields
            quiz_type:        cfg.quizType || 'CASH',
            game_format:      cfg.format   || null,
            stack_depth:      cfg.stack    || null,
            hero_position:    cfg.heroPosition,
            villain_position: cfg.threeBetPosition || cfg.openPosition || null,
            scenario_node:    node,
            action_type:      '4-Bet',
            training_filter:  cfg.filterMode || null,
            bet_type:         cfg.betType,

            hand_combo:       combo,
            hand_class:       handClass,
            suitedness:       suitedness,
            hand_category:    category,
            rank_cluster:     cluster,

            correct_action:    correctAction,
            correct_frequency: actualFrequency,
            selected_action:   selectedAction,
            selected_frequency: chosenFrequency,
            is_correct:        isCorrect,
            error_type:        errorType,
            answered_at:       new Date().toISOString(),
            question_number:   gameState.questionCount + 1,
          });
          console.log('4bet answer recorded in QuizAnalytics:', recorded);
        } catch (error) {
          console.error('Failed to record 4bet answer:', error);
        }
        
        const isLastQuestion = (gameState.questionCount + 1) >= gameState.gameConfig.totalHands;
        
        // IMPORTANT FIX: Create explicit hand data that won't be lost in navigation
        const selectedHand = {
          card1: `${hand.card1Value}${hand.card1Suit}`,
          card2: `${hand.card2Value}${hand.card2Suit}`
        };
        
        // Log the hand data we're sending
        console.log('Hand data for navigation:', selectedHand);
        
        // Create navigation state with our explicit hand data and preserve betType
        const navigationState = {
          betType: gameState.gameConfig.betType, // Explicitly include betType
          config: {
            position: gameState.gameConfig.heroPosition,
            betType: gameState.gameConfig.betType, 
            openPosition: gameState.gameConfig.openPosition,
            threeBetPosition: gameState.gameConfig.threeBetPosition,
            range: rangePath,
            actionTypeDisplay: '4BET'
          },
          heroPosition: hand.heroPosition || gameState.gameConfig.heroPosition,
          selectedHand: selectedHand,
          frequency: chosenFrequency,
          actualFrequency,
          isCorrect,
          isLastQuestion,
          finalScore: {
            correctAnswers: gameState.correctAnswers + (isCorrect ? 1 : 0),
            totalQuestions: gameState.gameConfig.totalHands
          },
          range: rangeData
        };
        
        // Update game state
        setGameState(prev => ({
          ...prev,
          questionCount: prev.questionCount + 1,
          correctAnswers: prev.correctAnswers + (isCorrect ? 1 : 0),
          currentHand: hand,
          lastAction: action,
          isCorrect,
          currentRange: newRange,
          currentRangeData: {
            path: rangePath,
            data: rangeData,
            timestamp: new Date().getTime()
          },
          pendingNavigation: {
            path: '/answer',
            state: navigationState
          }
        }));
        
        return { isCorrect, actualFrequency, chosenFrequency, hand: formattedHand };
      }
      
      // Original code for non-4bet scenarios
      const { newRange, rangeData } = await loadRange(rangePath);
      const formattedHand = formatHand(hand);
      
      // Get actual frequency and check correctness
      let actualFrequency = newRange.has(formattedHand) ? newRange.get(formattedHand) : 0;
      
      let isCorrect = false;
      if (chosenFrequency === 0) {
        isCorrect = actualFrequency === 0;
      } else if (chosenFrequency === 50) {
        isCorrect = actualFrequency > 0 && actualFrequency < 100;
      } else if (chosenFrequency === 100) {
        isCorrect = actualFrequency === 100;
      }
  
      // Record answer in QuizAnalytics (rich form)
      try {
        const cfg           = gameState.gameConfig;
        const combo         = handToCombo(hand);
        const handClass     = handToClass(hand);
        const suitedness    = getSuitedness(hand);
        const category      = getHandCategory(handClass);
        const cluster       = getRankCluster(handClass);
        const node          = deriveScenarioNode(cfg);
        const correctAction  = deriveAction(cfg.betType, actualFrequency);
        const selectedAction = deriveAction(cfg.betType, chosenFrequency);
        const errorType      = isCorrect ? null : getErrorType(correctAction, selectedAction);

        const recorded = await quizAnalytics.recordAnswer({
          // Legacy compat
          position:         cfg.heroPosition,
          hand:             hand,
          chosenFrequency:  chosenFrequency,
          correctFrequency: actualFrequency,
          isCorrect:        isCorrect,

          // Rich fields
          quiz_type:        cfg.quizType || 'CASH',
          game_format:      cfg.format   || null,
          stack_depth:      cfg.stack    || null,
          hero_position:    cfg.heroPosition,
          villain_position: cfg.threeBetPosition || cfg.openPosition || null,
          scenario_node:    node,
          action_type:      correctAction,
          training_filter:  cfg.filterMode || null,
          bet_type:         cfg.betType,

          hand_combo:       combo,
          hand_class:       handClass,
          suitedness:       suitedness,
          hand_category:    category,
          rank_cluster:     cluster,

          correct_action:    correctAction,
          correct_frequency: actualFrequency,
          selected_action:   selectedAction,
          selected_frequency: chosenFrequency,
          is_correct:        isCorrect,
          error_type:        errorType,
          answered_at:       new Date().toISOString(),
          question_number:   gameState.questionCount + 1,
        });
        console.log('Answer recorded in QuizAnalytics:', recorded);
      } catch (error) {
        console.error('Failed to record answer:', error);
      }
  
      const isLastQuestion = (gameState.questionCount + 1) >= gameState.gameConfig.totalHands;
      
      // If this is the last question, finalize the quiz
      if (isLastQuestion) {
        console.log('Last question detected, finalizing quiz');
        try {
          const finalized = await quizAnalytics.finalizeCurrentQuiz();
          console.log('Quiz finalization result:', finalized);
        } catch (error) {
          console.error('Failed to finalize quiz:', error);
        }
      }
  
      // IMPORTANT FIX: Create explicit hand data for navigation and preserve betType
      const selectedHand = {
        card1: `${hand.card1Value}${hand.card1Suit}`,
        card2: `${hand.card2Value}${hand.card2Suit}`
      };
      
      console.log('Hand data for navigation:', selectedHand);
  
      // Build navigation state with our explicit hand data and preserve betType
      const navigationState = {
        betType: gameState.gameConfig.betType, // Explicitly include betType
        config: {
          position: gameState.gameConfig.heroPosition,
          betType: gameState.gameConfig.betType,
          openPosition: gameState.gameConfig.openPosition,
          range: rangePath
        },
        heroPosition: hand.heroPosition || gameState.gameConfig.heroPosition,
        selectedHand: selectedHand,
        frequency: chosenFrequency,
        actualFrequency,
        isCorrect,
        isLastQuestion,
        finalScore: {
          correctAnswers: gameState.correctAnswers + (isCorrect ? 1 : 0),
          totalQuestions: gameState.gameConfig.totalHands
        },
        range: rangeData
      };
      
      // Update game state
      setGameState(prev => ({
        ...prev,
        questionCount: prev.questionCount + 1,
        correctAnswers: prev.correctAnswers + (isCorrect ? 1 : 0),
        currentHand: hand,
        lastAction: action,
        isCorrect,
        currentRange: newRange,
        currentRangeData: {
          path: rangePath,
          data: rangeData,
          timestamp: new Date().getTime()
        },
        pendingNavigation: {
          path: '/answer',
          state: navigationState
        }
      }));
      
      return { isCorrect, actualFrequency, chosenFrequency, hand: formattedHand };
    } catch (error) {
      console.error('Error in checkAnswer:', error);
      return { isCorrect: false, error: error.message };
    }
  };

  const handleContinue = (locationState) => {
    console.log('handleContinue called with location state:', locationState);

    setGameState(prev => {
      // Use next question or current question number based on context
      const nextQuestionNum = prev.questionCount;
      const isLastQuestion =
        locationState?.isLastQuestion === true ||
        nextQuestionNum >= prev.gameConfig.totalHands;

      // Determine routes based on quiz type
      const isBlast = prev.gameConfig?.quizType === 'BLAST';
      const quizRoute = isBlast ? '/blast' : '/preflop';
      const resultsRoute = isBlast ? '/blast-results' : '/results';

      if (isLastQuestion) {
        return {
          ...prev,
          pendingNavigation: {
            path: resultsRoute,
            state: {
              correctAnswers: prev.correctAnswers,
              totalQuestions: prev.gameConfig.totalHands
            }
          }
        };
      }

      return {
        ...prev,
        pendingNavigation: {
          path: quizRoute,
          state: null
        }
      };
    });
  };

  const navigateToResults = () => {
    // Make sure quiz is finalized before navigating
    if (!quizFinalized) {
      finalizeQuiz();
    }

    // Calculate stats for immediate use
    const stats = {
      accuracy: gameState.questionCount > 0 
        ? Math.round((gameState.correctAnswers / gameState.questionCount) * 100)
        : 0,
      totalCorrect: gameState.correctAnswers,
      totalQuestions: gameState.questionCount
    };
    
    // Use pendingNavigation pattern and pass serializable stats
    setGameState(prev => ({
      ...prev,
      pendingNavigation: {
        path: '/results',
        state: {
          correctAnswers: prev.correctAnswers,
          totalQuestions: prev.gameConfig.totalHands,
          // Include calculated stats for immediate use
          stats: stats
        }
      }
    }));
  };

  const resetGame = () => {
    resetHandTracking();
    setGameState({
      questionCount: 0,
      correctAnswers: 0,
      currentHand: null,
      lastAction: null,
      isCorrect: false,
      pendingNavigation: null,
      gameConfig: DEFAULT_GAME_CONFIG,
      currentRange: new Map(),
      currentRangeData: null
    });
    setQuizFinalized(false);
    setQuizSaveState({
      lastSavedQuizId: null,
      isSaving: false
    });
    setQuizProcessed(false);
  };

  useEffect(() => {
    // Only navigate if there's pending navigation
    if (gameState.pendingNavigation) {
      // Navigate with the state that was already prepared in checkAnswer
      navigate(gameState.pendingNavigation.path, { 
        state: gameState.pendingNavigation.state 
      });
      
      // Clear the pendingNavigation to prevent cycles
      setGameState(prev => ({ ...prev, pendingNavigation: null }));
    }
  }, [gameState.pendingNavigation, navigate]);

  return (
    <GameContext.Provider value={{
      gameState,
      startNewGame,
      recordAnswer,
      recordBlastAnswer,
      checkAnswer,
      finalizeQuiz,
      navigateToResults,
      resetGame,
      quizFinalized,
      quizSaveState,
      quizProcessed,
      setQuizProcessed,
      handleContinue,
      quizAnalytics,
      getNextHandDirective,
      commitHandDirective,
      shouldForceBlastLiveHand,
      markBlastHandDealt,
      markHandShown,
      markHandResult
    }}>
      {children}
    </GameContext.Provider>
  );
};

export const useGame = () => {
  const context = useContext(GameContext);
  if (!context) {
    throw new Error('useGame must be used within a GameProvider');
  }
  return context;
};